import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CreditCard, Plus, FileText, Ban } from "lucide-react";
import { toast } from "sonner";

const money = (v) => `${Number(v || 0).toLocaleString("fr-FR")} Dhs`;
const METHODS = ["especes", "virement", "cheque", "autre"];

export default function PaymentsList() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canManage = hasRole("super_admin", "direction", "administration", "finance");
  const [rows, setRows] = useState([]);
  const [families, setFamilies] = useState([]);
  const [dlg, setDlg] = useState({ open: false });
  const [form, setForm] = useState({ family_id: "", method: "especes", reference: "", cash_account_id: "", allocs: {} });
  const [account, setAccount] = useState(null);
  const [cash, setCash] = useState([]);
  const [cancel, setCancel] = useState({ open: false, id: null, text: "" });

  const load = () => api.get("/finance/payments").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  useEffect(() => { load(); api.get("/families").then((r) => setFamilies(r.data)).catch(() => {}); api.get("/finance/cash-accounts").then((r) => setCash(r.data)).catch(() => {}); }, []);

  const openPdf = async (path) => { try { const r = await api.get(path, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const chooseFamily = async (fid) => {
    setForm((f) => ({ ...f, family_id: fid, allocs: {} }));
    try { const r = await api.get(`/finance/families/${fid}/account`); setAccount(r.data); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const unpaid = () => {
    if (!account) return [];
    const out = [];
    account.children.forEach((c) => (c.invoices || []).forEach((i) => { if (i.remaining > 0 && i.status !== "cancelled") out.push({ ...i, student_name: c.student_name }); }));
    return out;
  };
  const setAlloc = (id, v) => setForm((f) => ({ ...f, allocs: { ...f.allocs, [id]: v } }));
  const totalAlloc = Object.values(form.allocs).reduce((s, v) => s + Number(v || 0), 0);

  const submit = async () => {
    const allocations = Object.entries(form.allocs).filter(([, v]) => Number(v) > 0).map(([invoice_id, amount]) => ({ invoice_id, amount: Number(amount) }));
    if (!form.family_id || allocations.length === 0) { toast.error(t("allocRequired")); return; }
    try {
      const r = await api.post("/finance/payments", { family_id: form.family_id, method: form.method, amount: totalAlloc, reference: form.reference, cash_account_id: form.cash_account_id === "none" ? "" : form.cash_account_id, allocations });
      toast.success(`${r.data.receipt_number}`); setDlg({ open: false }); setForm({ family_id: "", method: "especes", reference: "", cash_account_id: "", allocs: {} }); setAccount(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const submitCancel = async () => {
    if (!cancel.text.trim()) { toast.error(t("reasonRequired")); return; }
    try { await api.post(`/finance/payments/${cancel.id}/cancel`, { reason: cancel.text.trim() }); toast.success(t("save")); setCancel({ open: false, id: null, text: "" }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!canManage) return <div className="p-8 text-center text-slate-400" data-testid="access-denied">{t("accessDenied")}</div>;

  return (
    <div data-testid="payments-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3"><CreditCard className="h-6 w-6 text-primary" /><h1 className="text-2xl font-display font-bold text-slate-900">{t("payments")}</h1></div>
        <Button className="gap-2" data-testid="record-payment-btn" onClick={() => setDlg({ open: true })}><Plus className="h-4 w-4" />{t("recordPayment")}</Button>
      </div>

      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70"><TableHead>{t("number")}</TableHead><TableHead>{t("family")}</TableHead><TableHead>{t("method")}</TableHead><TableHead className="text-right">{t("amount")}</TableHead><TableHead>{t("date")}</TableHead><TableHead>{t("status")}</TableHead><TableHead className="text-right">{t("actions")}</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">{t("noData")}</TableCell></TableRow> :
              rows.map((r) => (
                <TableRow key={r.id} data-testid="payment-row">
                  <TableCell className="font-medium text-slate-800">{r.number}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{r.family_name}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{t(`method_${r.method}`)}</TableCell>
                  <TableCell className="text-right font-semibold">{money(r.amount)}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{(r.date || "").slice(0, 10)}</TableCell>
                  <TableCell><Badge className={`border-0 text-xs ${r.status === "cancelled" ? "bg-slate-200 text-slate-400" : "bg-emerald-100 text-emerald-700"}`}>{r.status === "cancelled" ? t("inv_cancelled") : t("active")}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center gap-1 justify-end">
                      {r.receipt_id && <Button size="sm" variant="ghost" className="h-8" data-testid={`receipt-${r.id}`} onClick={() => openPdf(`/finance/receipts/${r.receipt_id}/pdf`)}><FileText className="h-4 w-4" /></Button>}
                      {r.status !== "cancelled" && <Button size="sm" variant="ghost" className="h-8 text-red-600" data-testid={`cancel-${r.id}`} onClick={() => setCancel({ open: true, id: r.id, text: "" })}><Ban className="h-4 w-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dlg.open} onOpenChange={(o) => setDlg({ open: o })}>
        <DialogContent className="max-w-lg" data-testid="record-payment-dialog">
          <DialogHeader><DialogTitle>{t("recordPayment")}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">{t("family")}</label>
                <Select value={form.family_id} onValueChange={chooseFamily}><SelectTrigger data-testid="rp-family"><SelectValue placeholder={t("selectFamily")} /></SelectTrigger>
                  <SelectContent>{families.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent></Select></div>
              <div><label className="text-xs text-slate-500">{t("method")}</label>
                <Select value={form.method} onValueChange={(v) => setForm((f) => ({ ...f, method: v }))}><SelectTrigger data-testid="rp-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{t(`method_${m}`)}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><label className="text-xs text-slate-500">{t("reference")}</label><Input data-testid="rp-reference" value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} /></div>
            <div><label className="text-xs text-slate-500">{t("linkCashAccount")}</label>
              <Select value={form.cash_account_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, cash_account_id: v }))}><SelectTrigger data-testid="rp-cash"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">{t("none")}</SelectItem>{cash.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
            {account && (
              <div data-testid="rp-invoices">
                <label className="text-xs text-slate-500">{t("unpaidInvoices")}</label>
                {unpaid().length === 0 ? <p className="text-sm text-slate-400 mt-1">{t("noData")}</p> :
                  unpaid().map((i) => (
                    <div key={i.id} className="grid grid-cols-12 gap-2 items-center mt-2" data-testid={`rp-line-${i.id}`}>
                      <span className="col-span-7 text-sm text-slate-700">{i.number} · {i.student_name} <span className="text-amber-600">({money(i.remaining)})</span></span>
                      <Input className="col-span-5" type="number" placeholder="0" value={form.allocs[i.id] || ""} onChange={(e) => setAlloc(i.id, e.target.value)} data-testid={`rp-alloc-${i.id}`} />
                    </div>
                  ))}
              </div>
            )}
            <div className="text-right font-semibold text-slate-800" data-testid="rp-total">{t("amount")}: {money(totalAlloc)}</div>
          </div>
          <DialogFooter><Button data-testid="rp-save-btn" onClick={submit}>{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancel.open} onOpenChange={(o) => setCancel((c) => ({ ...c, open: o }))}>
        <DialogContent data-testid="cancel-payment-dialog">
          <DialogHeader><DialogTitle>{t("cancel2")}</DialogTitle></DialogHeader>
          <Input placeholder={t("reason")} value={cancel.text} onChange={(e) => setCancel((c) => ({ ...c, text: e.target.value }))} data-testid="cancel-reason" />
          <DialogFooter><Button variant="destructive" onClick={submitCancel} data-testid="cancel-confirm-btn">{t("confirmReason")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
